import {type FC, useEffect, useRef, useState} from 'react';
import {
    BackButton,
    Body,
    MsgContainer,
    Content,
    Divider,
    HeaderRow,
    Label,
    Title,
    MediaImg,
    MediaVideo,
    ScrollableContent,
} from '../types/MessagePage.styled.ts';
import {formatDate} from '../../../utils/DateUtil.ts';
import type {MessageInfo} from '../types';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import useMessageStore from '../../../store/useMessageStore.ts';

interface MessageDetailPageProp {
    messageInfo: MessageInfo;
    onBackToList: () => void;
}

const MessageDetailPage: FC<MessageDetailPageProp> = ({messageInfo, onBackToList}) => {
    const STATIC_BASE_URL = import.meta.env.VITE_STATIC_FILE_URL;
    const [scrollPosition, setScrollPosition] = useState(0);
    const bodyRef = useRef<HTMLDivElement>(null);
    const {currentPage} = useMessageStore();

    const {
        ref: detailPageRef,
        focusKey: detailPageFocusKey,
        focusSelf: focusDetailPage,
    } = useFocusable({
        focusKey: 'message-detail-page',
        preferredChildFocusKey: 'back-button',
        saveLastFocusedChild: true,
        trackChildren: true,
        isFocusBoundary: true,
    });

    const {ref: backButtonRef, focused: backButtonFocused} = useFocusable({
        focusKey: 'back-button',
        onEnterPress: onBackToList,
    });

    const {ref: bodyScrollRef, focused: bodyScrollFocused} = useFocusable({
        focusKey: 'body-scroll',
        onArrowPress: (direction) => {
            if (direction === 'up') {
                const currentScroll = bodyRef.current?.scrollTop || 0;
                if (currentScroll <= 0) {
                    setFocus('back-button');
                    return false;
                }

                if (bodyRef.current) {
                    const scrollAmount = 50;
                    const newScroll = Math.max(0, currentScroll - scrollAmount);
                    bodyRef.current.scrollTop = newScroll;
                    setScrollPosition(newScroll);
                }
                return false;
            }

            if (direction === 'down') {
                if (bodyRef.current) {
                    const scrollAmount = 50;
                    const currentScroll = bodyRef.current.scrollTop;
                    const maxScroll = bodyRef.current.scrollHeight - bodyRef.current.clientHeight;
                    const newScroll = Math.min(maxScroll, currentScroll + scrollAmount);
                    bodyRef.current.scrollTop = newScroll;
                    setScrollPosition(newScroll);
                }
                return false;
            }

            return false;
        },
    });

    useEffect(() => {
        if (currentPage === 'detail') {
            setFocus('back-button');
        }
    }, [currentPage]);

    return (
        <MsgContainer ref={detailPageRef}>
            <BackButton ref={backButtonRef} $focused={backButtonFocused} onClick={onBackToList}>
                &lt; 목록보기
            </BackButton>
            {messageInfo && (
                <>
                    <HeaderRow>
                        <Label>어르신 성함</Label>
                        <Content>{messageInfo.senior_name}</Content>
                        <Label>발신자 명</Label>
                        <Content>{messageInfo.author}</Content>
                        <Label>수신일시</Label>
                        <Content>{formatDate(messageInfo.created_at)}</Content>
                    </HeaderRow>
                    <Divider />
                    <Title>{messageInfo.title}</Title>
                    <Divider dashed />
                    <Body ref={bodyScrollRef} $focused={bodyScrollFocused}>
                        <ScrollableContent ref={bodyRef}>
                            {messageInfo.media?.endsWith('.jpg') ||
                            messageInfo.media?.endsWith('.png') ||
                            messageInfo.media?.endsWith('.jpeg') ? (
                                <MediaImg
                                    src={`${STATIC_BASE_URL}${messageInfo.media}`}
                                    alt={`${messageInfo.media}`}
                                    className="img-fluid w-100"
                                    style={{
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : messageInfo.media?.endsWith('.mp4') ||
                              messageInfo.media?.endsWith('.webm') ||
                              messageInfo.media?.endsWith('.ogg') ||
                              messageInfo.media?.endsWith('.avi') ||
                              messageInfo.media?.endsWith('.mkv') ? (
                                <MediaVideo
                                    src={`${STATIC_BASE_URL}${messageInfo.media}`}
                                    controls
                                    autoPlay={true}
                                    className="video-fluid w-100"
                                    style={{
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : null}
                            <Content>{messageInfo.content}</Content>
                        </ScrollableContent>
                    </Body>
                </>
            )}
        </MsgContainer>
    );
};

export default MessageDetailPage;
